/**
 * @file index.jsx
 * @description
 * Punto de entrada principal de la aplicación React.
 * Configura e inicializa Redux, React Router, contexto de configuración (`Main`) y notificaciones (`ToastContainer`).
 * 
 * @dependencies
 * - React & ReactDOM
 * - react-router-dom para enrutamiento
 * - react-redux para gestión de estado global
 * - react-toastify para notificaciones
 * - Configuración de rutas: `AppRoute`
 * - Contexto global de configuración: `Main`
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useRoutes, useLocation } from "react-router-dom";
import { Provider } from 'react-redux';

import AppRoute from 'config/app-route.jsx';
import { store } from 'store/index.js';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Main from './main'; // Contexto principal (Dark Mode, RTL, layout, etc.)
import 'helpers/initFA'; // Inicializa FontAwesome
import './index.css'; // Estilos globales

/**
 * Componente principal de enrutamiento de la aplicación.
 * Define la lógica para renderizar rutas y limpiar clases en el DOM
 * cuando cambia la ubicación.
 *
 * @returns {JSX.Element}
 */
function App() {
	let element = useRoutes(AppRoute); // Hook para cargar las rutas definidas
	let location = useLocation(); // Detecta cambios de URL para efectos secundarios

	/**
	 * useEffect
	 * Se activa al cambiar de ruta para limpiar clases específicas del layout móvil.
	 */
	React.useEffect(() => {
		const elm = document.querySelector('.app');
    	if (elm) {
      		elm.classList.remove('app-sidebar-mobile-toggled');
    	}
  	}, [location]);

  	return element;
}

// Punto de montaje de React
const container = document.getElementById('root');
const root = createRoot(container);

/**
 * Renderiza toda la aplicación React con:
 * - Redux Store Provider
 * - React Router (BrowserRouter)
 * - AppContext (Main)
 * - ToastContainer para notificaciones globales
 */
root.render(
	<Provider store={store}>
    	<BrowserRouter>
			<Main>
				<App />
				<ToastContainer />
			</Main>
    	</BrowserRouter>
  	</Provider>
);